import { useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotification, useTranslate } from "@refinedev/core";

import { ApiError } from "@shared/types";
import requestErrorTreatment from "@shared/utils/requestErrorTreatment";
import { listAgreementsQueryKey } from "../../queries/listAgreements/query";
import { cloneAgreementService } from "./service";
import {
  CloneAgreementInput,
  CloneAgreementResponse,
  UseCloneAgreementMutationOptions,
} from "./types";

function buildCloneSummaryMessage(
  response: CloneAgreementResponse,
  translate: (key: string, options?: Record<string, unknown>) => string
) {
  const { counts } = response.duplicated;

  return translate("v1/agreement_admin.messages.clone.success", {
    name: response.name,
    products: counts.products,
    plans: counts.plans,
    landings: counts.landings,
    modules: counts.modules,
  });
}

export function useCloneAgreementMutation(
  options?: UseCloneAgreementMutationOptions
) {
  const navigate = useNavigate();
  const translate = useTranslate();
  const { open } = useNotification();
  const queryClient = useQueryClient();

  return useMutation<CloneAgreementResponse, ApiError, CloneAgreementInput>({
    mutationFn: cloneAgreementService,
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: listAgreementsQueryKey,
        refetchType: "all",
      });

      open?.({
        type: "success",
        message: buildCloneSummaryMessage(response, translate),
      });

      navigate(`/agreement/edit/${response.agreement_id}`);
    },
    onError: (error: ApiError) => {
      requestErrorTreatment(error, open);
    },
    ...options,
  });
}
